import React, { useState, useEffect } from 'react';
import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import { Button } from 'ui/Button';
import { Switch } from '@mantine/core';
import { ReactComponent as IconDone } from '../img/iconDone.svg';
import styles from './TelegramNotificationModal.module.scss';
import {
  useGetTelegramSettingsQuery,
  useSetTelegramNotificationsMutation,
} from 'redux/Api/profileApi/ProfileApi';

interface TelegramNotificationModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const TelegramNotificationModal: React.FC<TelegramNotificationModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    status_notifications_enabled: true,
    operation_notifications_enabled: true,
    balance_notifications_enabled: true,
  });

  const { data: telegramSettings, refetch } = useGetTelegramSettingsQuery(undefined, {
    skip: !isOpen,
  });
  const [updateNotifications, { isLoading }] = useSetTelegramNotificationsMutation();

  useEffect(() => {
    if (telegramSettings) {
      setSettings({
        notifications_enabled: telegramSettings.notifications_enabled ?? true,
        status_notifications_enabled:
          telegramSettings.status_notifications_enabled ?? true,
        operation_notifications_enabled:
          telegramSettings.operation_notifications_enabled ?? true,
        balance_notifications_enabled:
          telegramSettings.balance_notifications_enabled ?? true,
      });
    }
  }, [telegramSettings]);

  const handleSelectAll = () => {
    setSettings({
      notifications_enabled: true,
      status_notifications_enabled: true,
      operation_notifications_enabled: true,
      balance_notifications_enabled: true,
    });
  };

  const handleSave = async () => {
    try {
      await updateNotifications(settings).unwrap();
      await refetch();
      setIsOpen(false);
    } catch (error) {
      console.error('Ошибка при сохранении настроек:', error);
    }
  };

  const handleSwitchChange = (key: keyof typeof settings, value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Modal setOpen={setIsOpen} open={isOpen}>
      <div className={styles.root}>
        <div className={styles.header}>
          <Title title='Настройка оповещения в Телеграм' />
        </div>

        <div className={styles.selectAll}>
          <span onClick={handleSelectAll} className={styles.selectAllLink}>
            Выбрать все
          </span>
        </div>

        <div className={styles.settingsContainer}>
          <div className={styles.settingItem}>
            <div className={styles.settingContent}>
              <div className={styles.settingTitle}>Статусы</div>
              <div className={styles.settingDescription}>
                Переведем выплату исполнителю, даже если сервис ФНС не отвечает
              </div>
            </div>
            <Switch
              color='#002CFB'
              styles={{
                track: {
                  width: '44px',
                  height: '24px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: settings.status_notifications_enabled
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={settings.status_notifications_enabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleSwitchChange(
                  'status_notifications_enabled',
                  event.currentTarget.checked,
                );
              }}
            />
          </div>

          <div className={styles.settingItem}>
            <div className={styles.settingContent}>
              <div className={styles.settingTitle}>Операции</div>
              <div className={styles.settingDescription}>Оплата заказов</div>
            </div>
            <Switch
              color='#002CFB'
              styles={{
                track: {
                  width: '44px',
                  height: '24px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: settings.operation_notifications_enabled
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={settings.operation_notifications_enabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleSwitchChange(
                  'operation_notifications_enabled',
                  event.currentTarget.checked,
                );
              }}
            />
          </div>

          <div className={styles.settingItem}>
            <div className={styles.settingContent}>
              <div className={styles.settingTitle}>Пополнение</div>
              <div className={styles.settingDescription}>Пополнение баланса компании</div>
            </div>
            <Switch
              color='#002CFB'
              styles={{
                track: {
                  width: '44px',
                  height: '24px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: settings.balance_notifications_enabled
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={settings.balance_notifications_enabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleSwitchChange(
                  'balance_notifications_enabled',
                  event.currentTarget.checked,
                );
              }}
            />
          </div>
        </div>

        <Button
          onClick={handleSave}
          className={styles.saveButton}
          disabled={isLoading}
          theme='filed'>
          <div className={styles.saveButtonText}>
            Сохранить <IconDone />
          </div>
        </Button>
      </div>
    </Modal>
  );
};
